import { useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { useApi, githubAuthApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useTemplateSecrets } from '@backstage/plugin-scaffolder-react';

const SEGMENT_REGEX = /^[a-z0-9]+$/;
const COMPOSED_REGEX = /^[a-z0-9]+(-[a-z0-9]+)+$/;
const MAX_NAME_LENGTH = 50;

/**
 * Custom scaffolder field that:
 * - Reads bu (a Domain entity ref)/product/component/type from sibling fields
 *   in formContext.formData
 * - Resolves the Domain's univerus.com/bu-code annotation into the bu name
 *   segment, so the BU list is never hardcoded in the template
 * - Composes and validates the repository name live
 * - Gates submission on active univerus GitHub org membership
 * - Acquires the user's GitHub OAuth token and stores it as a template secret
 *   (USER_OAUTH_TOKEN) so publish:github creates the repo under their account
 * - Writes repoName, githubLogin, and buCode to form data
 */
export const RepositoryNameFields = ({
  formData: _formData,
  onChange,
  formContext,
}: any) => {
  const githubAuth = useApi(githubAuthApiRef);
  const catalogApi = useApi(catalogApiRef);
  const { setSecrets } = useTemplateSecrets();

  const buRef: string = formContext.formData?.bu ?? '';
  const product: string = formContext.formData?.product ?? '';
  const component: string = formContext.formData?.component ?? '';
  const type: string = formContext.formData?.type ?? '';

  const [buCode, setBuCode] = useState<string>('');
  const [buTitle, setBuTitle] = useState<string>('');
  const [buError, setBuError] = useState<string>('');

  useEffect(() => {
    // Clear synchronously on every selection change so the composed name and
    // the membership gate can never run against the previous Domain's code
    // while the lookup below is in flight.
    setBuCode('');
    setBuTitle('');
    setBuError('');
    if (!buRef) {
      return undefined;
    }
    let cancelled = false;
    const resolveBuCode = async () => {
      try {
        const entity = await catalogApi.getEntityByRef(buRef);
        const code =
          entity?.metadata.annotations?.['univerus.com/bu-code'] ?? '';
        if (!code) {
          throw new Error(
            'The selected business unit has no univerus.com/bu-code annotation in the catalog. Contact Platform Engineering.',
          );
        }
        if (!cancelled) {
          setBuCode(code.toLowerCase());
          setBuTitle(entity?.metadata.title ?? entity?.metadata.name ?? '');
        }
      } catch (err) {
        if (!cancelled) {
          setBuCode('');
          setBuTitle('');
          setBuError(
            err instanceof Error
              ? err.message
              : 'Failed to resolve the selected business unit',
          );
        }
      }
    };
    resolveBuCode();
    return () => {
      cancelled = true;
    };
  }, [buRef, catalogApi]);

  const bu = buCode;

  const [githubLogin, setGithubLogin] = useState<string>('');
  const [gateError, setGateError] = useState<string>('');
  const [gateLoading, setGateLoading] = useState<boolean>(false);
  const loginRef = useRef<string | null>(null);
  // Ref so the async verify() closure always calls the current onChange even if
  // the parent re-renders and passes a new reference mid-flight.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const productError =
    product && !SEGMENT_REGEX.test(product)
      ? 'Only lowercase letters (a-z) and digits (0-9) — no hyphens inside a segment'
      : '';
  const componentError =
    component && !SEGMENT_REGEX.test(component)
      ? 'Only lowercase letters (a-z) and digits (0-9) — no hyphens inside a segment'
      : '';

  const segments = [bu, product, component, ...(type ? [type] : [])].filter(
    Boolean,
  );
  const repoName = segments.join('-');
  const allRequiredPresent = !!(bu && product && component);
  const nameOverLength = repoName.length >= MAX_NAME_LENGTH;
  // Only flag pattern errors once all required segments are present — partial
  // input (e.g. only bu filled) would fail the regex and show a spurious error.
  const namePatternInvalid =
    allRequiredPresent && !COMPOSED_REGEX.test(repoName);

  const inputsValid = !!(
    bu &&
    product &&
    !productError &&
    component &&
    !componentError &&
    repoName &&
    !nameOverLength &&
    !namePatternInvalid
  );

  useEffect(() => {
    if (!inputsValid) {
      loginRef.current = null;
      setGithubLogin('');
      setGateError('');
      setGateLoading(false);
      onChangeRef.current({
        repoName: undefined,
        githubLogin: undefined,
        buCode: undefined,
      });
      return undefined;
    }

    // Already authenticated — just sync the updated repoName
    if (loginRef.current) {
      onChangeRef.current({
        repoName,
        githubLogin: loginRef.current,
        buCode,
      });
      return undefined;
    }

    // Fresh auth + membership check
    let cancelled = false;
    setGateLoading(true);
    setGateError('');

    const verify = async () => {
      try {
        const token = await githubAuth.getAccessToken(['read:org', 'repo']);

        const userRes = await fetch('https://api.github.com/user', {
          headers: {
            Authorization: `token ${token}`,
            Accept: 'application/vnd.github+json',
          },
        });
        if (!userRes.ok) {
          throw new Error(`GitHub user lookup failed (HTTP ${userRes.status})`);
        }
        const user = await userRes.json();

        const memberRes = await fetch(
          'https://api.github.com/user/memberships/orgs/univerus',
          {
            headers: {
              Authorization: `token ${token}`,
              Accept: 'application/vnd.github+json',
            },
          },
        );
        if (!memberRes.ok) {
          throw new Error(
            'Could not verify univerus org membership. Ensure you have a GitHub account and are an active org member.',
          );
        }
        const membership = await memberRes.json();
        if (membership.state !== 'active') {
          throw new Error(
            `Your univerus org membership is not active (state: ${membership.state}). Contact your org admin.`,
          );
        }

        if (!cancelled) {
          setSecrets({ USER_OAUTH_TOKEN: token });
          loginRef.current = user.login;
          setGithubLogin(user.login);
          setGateLoading(false);
          onChangeRef.current({ repoName, githubLogin: user.login, buCode });
        }
      } catch (err) {
        if (!cancelled) {
          setGateError(
            err instanceof Error ? err.message : 'GitHub verification failed',
          );
          setGateLoading(false);
          onChangeRef.current({
            repoName: undefined,
            githubLogin: undefined,
            buCode: undefined,
          });
        }
      }
    };

    verify();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputsValid, repoName]);

  const nameHelperText = () => {
    if (productError) return productError;
    if (componentError) return componentError;
    if (nameOverLength)
      return `Name must be under ${MAX_NAME_LENGTH} characters (currently ${repoName.length})`;
    if (namePatternInvalid)
      return 'Invalid — ensure each segment is lowercase alphanumeric';
    if (buCode && buTitle)
      return `Composed from the fields above. Prefix "${buCode}" comes from ${buTitle} (business unit code ${buCode.toUpperCase()}).`;
    return 'Composed from the fields above';
  };

  const nameHasError =
    nameOverLength || namePatternInvalid || !!productError || !!componentError;

  return (
    <Box>
      <FormControl fullWidth margin="normal">
        <TextField
          label="Repository Name (preview)"
          value={repoName || '—'}
          InputProps={{ readOnly: true }}
          variant="outlined"
          helperText={nameHelperText()}
          error={nameHasError}
        />
      </FormControl>

      {buError && (
        <Alert severity="error" style={{ marginTop: 8, marginBottom: 8 }}>
          <AlertTitle>Business Unit Lookup Failed</AlertTitle>
          {buError}
        </Alert>
      )}

      {inputsValid && gateLoading && (
        <Box display="flex" alignItems="center" my={1}>
          <CircularProgress size={16} />
          <Box ml={1}>
            <Typography variant="body2">
              Verifying univerus GitHub org membership...
            </Typography>
          </Box>
        </Box>
      )}

      {gateError && (
        <Alert severity="error" style={{ marginTop: 8, marginBottom: 8 }}>
          <AlertTitle>GitHub Verification Failed</AlertTitle>
          {gateError}
        </Alert>
      )}

      {inputsValid && githubLogin && !gateLoading && (
        <Alert severity="success" style={{ marginTop: 8, marginBottom: 8 }}>
          <AlertTitle>Repository will be created by @{githubLogin}</AlertTitle>
          <Typography variant="body2">
            The repository will be created in the <strong>univerus</strong> org
            using your GitHub account. The initial commit will be attributed to
            you.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};
